/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import { CalculatorResult, getValidityState, ParameterModifier, ParameterName, ParameterValueElement } from './calculatorTypes';
import CalculatorConditions, { GasType, GasProperties, PsychrometerType, PsychrometerProperties } from './CalculatorConditions';
import * as calc from './CalculatorFunctions';
import UnitConverter from '../units/UnitConverter';
// import { resourceLimits } from 'worker_threads';

class Calculator {
  private static _instance: Calculator;
  public static get instance() {
    return this._instance || (this._instance = new this());
  }

  private unitConverter: UnitConverter;

  constructor() {
    this.unitConverter = new UnitConverter();
  }

  /**
   * Recalculates all parameter values based on one known parameter.
   *
   * @param {CalculatorResult|undefined} previousResult - The parameters before the calculation, undefined if this is the first calculation
   * @param {ParameterName} changedParameter - The name of the parameter whose value was changed
   * @param {ParameterValueElement} element - Changed parameter element, i.e. 'value' or 'tolerance'
   * @param {ParameterModifier} value - The new value of the parameter's element
   *
   * @returns {CalculatorResult|undefined} - The newly computed values, or null if failed to calculate (not expected to happen)
   */
  public calculate = (
    previousResult: CalculatorResult | undefined,
    selectedHumidityParameter: ParameterName,
    changedParameter: ParameterName,
    element: ParameterValueElement,
    value: ParameterModifier,
    elevationMode: boolean
  ): CalculatorResult | undefined => {
    let result = new CalculatorResult();

    // Temporary placeholder calculation: spit out random values, used for simulating UI changes
    const params = result.parameters;
    if (previousResult === undefined) {
      return undefined;
    }

    Object.keys(params).forEach((key) => {
      const k = key as ParameterName;
      if (previousResult) {
        params[k] = previousResult.parameters[k] as any;
      }
    });

    let baseHumidityParameter: ParameterName;

    // If temperature, pressure or elevation is changed, use waterConcentration as base base humidity parameter
    if (
      (changedParameter === 'temperature' && element === 'value') ||
      (changedParameter === 'pressure' && element === 'value') ||
      (changedParameter === 'elevation' && element === 'value')
    ) {
      baseHumidityParameter = 'waterConcentrationWetBasis';
    } else {
      baseHumidityParameter = selectedHumidityParameter;
    }

    const temperature = changedParameter === 'temperature' && element === 'value' ? (value as number) : previousResult.parameters.temperature.value;
    //console.log(temperature, result.parameters.temperature.value);
    const pressure =
      changedParameter === 'pressure' && element === 'value'
        ? (value as number)
        : changedParameter === 'elevation' && element === 'value'
        ? (this.unitConverter.convert('quantity.elevation', value as number, 'm', 'hPa', previousResult, false) as number)
        : elevationMode ? (this.unitConverter.convert('quantity.elevation', previousResult.parameters.elevation.value as number, 'm', 'hPa', previousResult, false) as number) : previousResult.parameters.pressure.value;
    // const pressure = (changedParameter === 'pressure' || changedParameter === 'elevation') && element === 'value' ? (value as number) : previousResult.parameters.pressure.value;

    const elevation = changedParameter === 'elevation' ? (value as number) : changedParameter === 'pressure' ? (this.unitConverter.convert('quantity.pressure', value as number, 'hPa', 'm', previousResult, false) as number) : previousResult.parameters.elevation.value;

    const gasType = changedParameter === 'gasType' && element === 'value' ? (value as GasType) : (previousResult.parameters.gasType.value as GasType);
    const g: GasProperties = CalculatorConditions.propertiesForGas(gasType as GasType);
    // console.log(`Calculator.calculate(): gas properties for ${gasType} = `, g);

    const psychrometer = changedParameter === 'psychrometer' && element === 'value' ? value : previousResult.parameters.psychrometer.value;
    const p: PsychrometerProperties = CalculatorConditions.propertiesForPsychrometer(psychrometer as PsychrometerType);
    //console.log(`Calculator.calculate(): psychrometer properties for ${psychrometer} = `, p);

    const saturationPressure = calc.pws(temperature, pressure, gasType);
    const saturationPressure_ice = calc.pis(temperature, pressure, gasType);
    result.parameters.waterVaporSaturationPressure.value = temperature < 0 ? saturationPressure_ice : saturationPressure;

    const humidityParameterValue =
      changedParameter === baseHumidityParameter && element === 'value'
        ? (value as number)
        : (previousResult.parameters[baseHumidityParameter].value as number);
    const SelectedTolerance =
      changedParameter === selectedHumidityParameter && element === 'tolerance'
        ? (value as number)
        : previousResult.parameters[selectedHumidityParameter].tolerance;

    let humidityParameterTolerance;
    let vaporPressure: number;
    let vaporPressureMin: number;
    let vaporPressureMax: number;

    const calculatableHumidityParametersList = [
      'relativeHumidity',
      'relativeHumidityDewFrost',
      'dewPointTemperature',
      'dewFrostPointTemperature',
      'waterVaporPressure',
      'absoluteHumidity',
      'mixingRatio',
      'waterConcentration',
      'waterConcentrationWetBasis',
      'wetBulbTemperature',
      'enthalpy',
      'gasDensity',
      'specificVolume',
    ];

    const HumidityParameterToVaporPressure = (humidityParameter: ParameterName, ParameterValue: number) => {
      switch (humidityParameter) {
        case 'relativeHumidity':
          return calc.rhToPw(ParameterValue, saturationPressure);
        case 'relativeHumidityDewFrost':
          return temperature < 0
            ? calc.rhiToPw(ParameterValue, calc.pis(temperature, pressure, gasType))
            : calc.rhToPw(ParameterValue, saturationPressure);
        case 'dewPointTemperature':
          return calc.tdToPw(ParameterValue, pressure, gasType) as number;
        case 'dewFrostPointTemperature':
          return calc.tdfToPw(ParameterValue, pressure, gasType) as number;
        case 'waterVaporPressure':
          return ParameterValue;
        case 'gasDensity':
          return ParameterValue;
        case 'specificVolume':
          return ParameterValue;
        case 'absoluteHumidity':
          return calc.aToPw(ParameterValue, temperature) as number;
        case 'mixingRatio':
          return calc.xToPw(ParameterValue, pressure, g.molarMass) as number;
        case 'waterConcentration':
          return calc.ppmvToPw(ParameterValue, pressure) as number;
        case 'waterConcentrationWetBasis':
          return calc.ppmvwetToPw(ParameterValue, pressure) as number;
        case 'wetBulbTemperature':
          return calc.twToPw(ParameterValue, temperature, pressure, p, gasType) as number;
        case 'enthalpy':
          return calc.hToPw(ParameterValue, temperature, 0, pressure, g.molarMass, g.specificHeatCapacity) as number;
        default:
          console.error('Invalid humidity parameter', humidityParameter);
          return;
      }
    };

    const VaporPressureToHumidityParameter = (pw: number, humidityParameter: ParameterName) => {
      switch (humidityParameter) {
        case 'relativeHumidity':
          return calc.pwToRh(pw, saturationPressure);
        case 'relativeHumidityDewFrost':
          return temperature >= 0 ? calc.pwToRh(pw, saturationPressure) : calc.pwToRhi(pw, calc.pis(temperature, pressure, gasType));
        case 'dewPointTemperature':
          return calc.pwToTd(pw, temperature, pressure, gasType);
        case 'dewFrostPointTemperature': {
          const result = calc.pwToTd(pw, temperature, pressure, gasType);
          return result >= 0 ? result : calc.pwToTdf(pw, temperature, pressure, gasType);
        }
        case 'waterVaporPressure':
          return pw;
        case 'gasDensity':
          return calc.gasDensity(temperature, pressure, g.molarMass, vaporPressure);
        case 'specificVolume':
          return calc.specificVolume(temperature, pressure, g.molarMass, vaporPressure);
        case 'absoluteHumidity':
          return calc.pwToA(pw, temperature);
        case 'mixingRatio':
          return calc.pwToX(pw, pressure, g.molarMass);
        case 'waterConcentration':
          return calc.pwToPpmv(pw, pressure);
        case 'waterConcentrationWetBasis':
          return calc.pwToPpmvwet(pw, pressure);
        case 'wetBulbTemperature':
          return calc.pwToTw(pw, temperature, pressure, p, gasType);
        case 'enthalpy':
          return calc.pwToH(pw, temperature, 0, pressure, g.molarMass, g.specificHeatCapacity);
        default:
          console.error('Invalid humidity parameter', humidityParameter);
          return;
      }
    };

    const calculateTolerance = (min: number, nominal: number, max: number) =>
      nominal - min > max - nominal ? nominal - min : ((max - nominal) as number);

    // Calculate vapor pressure of base humidity parameter. Use tolerance of selected humidity parameter
    if (selectedHumidityParameter !== baseHumidityParameter) {
      vaporPressure = HumidityParameterToVaporPressure(baseHumidityParameter, humidityParameterValue) as number;
      const newValue = VaporPressureToHumidityParameter(vaporPressure as number, selectedHumidityParameter) as number;
      vaporPressureMin = HumidityParameterToVaporPressure(selectedHumidityParameter, newValue - SelectedTolerance) as number;
      vaporPressureMax = HumidityParameterToVaporPressure(selectedHumidityParameter, newValue + SelectedTolerance) as number;
      const baseHPMin = VaporPressureToHumidityParameter(vaporPressureMin, baseHumidityParameter) as number;
      const baseHPMax = VaporPressureToHumidityParameter(vaporPressureMax, baseHumidityParameter) as number;
      humidityParameterTolerance = calculateTolerance(baseHPMin, humidityParameterValue, baseHPMax);

      result.parameters[baseHumidityParameter].value = humidityParameterValue;
      result.parameters[baseHumidityParameter].tolerance = humidityParameterTolerance;
      result.parameters[selectedHumidityParameter].tolerance = SelectedTolerance;
    } else {
      humidityParameterTolerance = SelectedTolerance;
      vaporPressure = HumidityParameterToVaporPressure(baseHumidityParameter, humidityParameterValue) as number;
      vaporPressureMin = HumidityParameterToVaporPressure(selectedHumidityParameter, humidityParameterValue - SelectedTolerance) as number;
      vaporPressureMax = HumidityParameterToVaporPressure(selectedHumidityParameter, humidityParameterValue + SelectedTolerance) as number;

      result.parameters[baseHumidityParameter].value = humidityParameterValue;
      result.parameters[baseHumidityParameter].tolerance = humidityParameterTolerance;
    }

    // Calculate other humidity parameters
    Object.keys(result.parameters).forEach((key) => {
      const k = key as ParameterName;
      if (calculatableHumidityParametersList.includes(k)) {
        if (k !== baseHumidityParameter) {
          if (baseHumidityParameter !== selectedHumidityParameter && k === selectedHumidityParameter) {
            result.parameters[k].value = VaporPressureToHumidityParameter(vaporPressure, k) as number;
          } else {
            const nominal = VaporPressureToHumidityParameter(vaporPressure, k) as number;
            const min = VaporPressureToHumidityParameter(vaporPressureMin, k) as number;
            const max = VaporPressureToHumidityParameter(vaporPressureMax, k) as number;

            result.parameters[k].value = nominal as number;
            result.parameters[k].tolerance = calculateTolerance(min, nominal, max) as number;
          }
        }
      }
    });

    if (changedParameter === 'pressure' || changedParameter === 'elevation') {
      params['pressure']['value'] = pressure as any;
      params['elevation']['value'] = elevation as any;
    } else {
      params[changedParameter][element] = value as any;
    }

    const severityOrder = { on: 1, near: 2, frost: 3, off: 4 };

    // Validity states
    if (result.parameters.waterVaporPressure.value > result.parameters.pressure.value) {
      result.validityStates.push(getValidityState('vaporPressure'));
    }
    if (result.parameters.relativeHumidity.value > 100) {
      result.validityStates.push(getValidityState('condensationExceeded'));
    }
    if (result.parameters.relativeHumidity.value + result.parameters.relativeHumidity.tolerance >= 100) {
      result.validityStates.push(getValidityState('condensationNear'));
    }
    if (result.parameters.relativeHumidityDewFrost.value + result.parameters.relativeHumidityDewFrost.tolerance >= 100) {
      result.validityStates.push(getValidityState('frostForming'));
    }
    if (result.parameters.temperature.value < 0) {
      result.validityStates.push(getValidityState('rhiCalcInUse'));
    }
    if (result.parameters.dewFrostPointTemperature.value < 0) {
      result.validityStates.push(getValidityState('tdfCalcInUse'));
    }
    if (
      (['gas.Air', 'gas.N2'].includes(gasType) && result.parameters.pressure.value > 20000) ||
      (!['gas.Air', 'gas.N2'].includes(gasType) && result.parameters.pressure.value > 2000)
    ) {
      result.validityStates.push(getValidityState('pressureHigh'));
    }
    if (result.parameters.temperature.value > 100) {
      result.validityStates.push(getValidityState('temperatureHigh'));
    }

    result.validityStates = result.validityStates.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

    return result;
  };
}

export default Calculator.instance;
